import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { CJEvent } from '@teetsh/app/src/types/event';
import { Actions } from 'containers/CahierJournal/DayColumn/Seance/Actions';
import { ClassicEvent } from 'containers/CahierJournal/DayColumn/Seance/Classic';
import { Bilan } from 'containers/CahierJournal/DayColumn/Seance/Bilan';
import {
  ADD_EVENT,
  BILAN_EVENT,
  BREAK_EVENT,
  CLASSIC_EVENT,
  RAPPEL_EVENT,
  RITUEL_EVENT,
} from '@teetsh/app/src/containers/constants';
import { BOLabels } from '@teetsh/app/src/types/user';
import { AddEventItem } from 'containers/CahierJournal/DayColumn/Seance/AddEventItem';
import Card from '@teetsh/app/src/components/Card';
import clsx from 'clsx';

import { Classroom } from '@teetsh/app/src/types/classroom';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { useClassroom } from '@teetsh/app/src/containers/CahierJournal/DayColumn/ClassroomContext';
import { useOpenUpdateCJSeanceModal } from 'containers/CahierJournal/UpdateCJSeanceModal/useOpenUpdateCJSeanceModal';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { AtelierEvent } from './Atelier';
import { Footer } from 'containers/CahierJournal/DayColumn/Seance/Footer';
import { NewAttachmentLinks } from 'components/Attachments/NewAttachmentLinks';
import { useFieldsToDisplay } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay/atom';
import { useCJConfigurationDisplay } from '@teetsh/app/src/containers/CahierJournal/useCJConfigurationDisplay';

interface Props {
  className?: string;
  event: CJEvent;
  isFirstEventInRow: boolean;
  isLastEventInRow: boolean;
  isHighlighted: boolean;
  boLabels: BOLabels;
  condensed: boolean;
  collapsed: boolean;
  onReorder: (direction: -1 | 1) => void;
  onRotateGroups?: () => void;
  classrooms: Classroom[];
  classroomsOrg: ClassroomsOrg;
  onChange: (v: CJEvent) => void;
  rowIndex: number;
}

export function OneCard(props: Props) {
  const {
    className = '',
    event,
    isFirstEventInRow,
    isLastEventInRow,
    isHighlighted,
    onReorder,
    onRotateGroups,
    boLabels,
    condensed,
    classrooms,
    classroomsOrg,
    rowIndex,
  } = props;

  const [collapsed, setCollpased] = useState<boolean>(props.collapsed);
  useEffect(() => {
    setCollpased(props.collapsed);
  }, [props.collapsed]);

  const { guestMode } = useUser();
  const classroom = useClassroom();
  const fieldsToDisplay = useFieldsToDisplay();
  const { density } = useCJConfigurationDisplay();
  const openModifyCJSeanceModal = useOpenUpdateCJSeanceModal();
  const { t } = useTranslation(['cahierJournal']);
  const { type } = event;
  if (type === 'loading') {
    return <div tw="w-full rounded h-16" className="tsh-skeleton" />;
  }
  if (type === ADD_EVENT) {
    return (
      <AddEventItem
        css={[
          rowIndex === 0 && tw`mb-2`,
          rowIndex !== 0 && (density === 'compact' ? tw`my-2` : tw`my-4`),
        ]}
        visible={false}
        start={event.start}
        end={event.end}
        classrooms={classrooms}
      />
    );
  }
  return (
    <Card
      id={`seance-${event.id.split('-')[0]}`}
      interactive={guestMode?.permission !== 'read'}
      css={[
        tw`p-0! flex-1 overflow-visible rounded-lg relative min-w-0`,
        isHighlighted && tw`animate-glow`,
      ]}
      className={clsx(className, 'group')}
      onClick={() =>
        openModifyCJSeanceModal({
          seance: event,
          classroomId: classroom.id,
        })
      }
    >
      {type === BILAN_EVENT && (
        <Bilan
          onChange={props.onChange}
          defaultTitle={t('cahierJournal:labels.bilan')}
          event={event}
          density={density}
        />
      )}
      {(type === CLASSIC_EVENT || type === RITUEL_EVENT) && (
        <>
          <ClassicEvent
            onChange={props.onChange}
            collapsed={collapsed}
            event={event}
            boLabels={boLabels}
            condensed={condensed}
            classroomsOrg={classroomsOrg}
            density={density}
          />
          {event.ateliers?.length > 0 && (
            <AtelierEvent
              css={[
                tw`px-4`,
                density === 'normal' && tw`pb-2`,
                event.status === 'done' && tw`opacity-50`,
              ]}
              collapsed={collapsed}
              event={event}
              boLabels={boLabels}
              condensed={condensed}
              classroomsOrg={classroomsOrg}
              onAtelierClick={atelierId => e => {
                e.stopPropagation();
                openModifyCJSeanceModal({
                  seance: event,
                  classroomId: classroom.id,
                  atelierId,
                });
                return;
              }}
            />
          )}
          {fieldsToDisplay?.includes('attachments') && !collapsed && (
            <NewAttachmentLinks
              css={[
                event.status === 'done' && tw`opacity-50`,
                density === 'compact' ? tw`py-1` : tw`py-2`,
                tw`px-4`,
              ]}
              attachments={event.attachments || []}
              preview
            />
          )}
        </>
      )}

      <Footer event={event} />

      {type === RAPPEL_EVENT && (
        <Bilan
          onChange={props.onChange}
          defaultTitle={t('cahierJournal:labels.reminders')}
          event={event}
          density={density}
        />
      )}
      {type === BREAK_EVENT && (
        <ClassicEvent
          onChange={props.onChange}
          collapsed={collapsed}
          event={event}
          boLabels={boLabels}
          condensed={condensed}
          classroomsOrg={classroomsOrg}
          density={density}
        />
      )}
      <Actions
        key={event.id}
        event={event}
        isFirstEventInRow={isFirstEventInRow}
        isLastEventInRow={isLastEventInRow}
        onReorder={onReorder}
        onRotateGroups={onRotateGroups}
      />
    </Card>
  );
}
